import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Play, Radio, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import EpisodeCard from "@/components/EpisodeCard";
import VideoModal from "@/components/VideoModal";
import { toast } from "sonner";

const SVN_LOGO = "https://customer-assets.emergentagent.com/job_c234235a-294b-44f9-bf35-e6004ec2a9f9/artifacts/bf6cv9t0_file_00000000b9b471fda4e6d506d62351f1.png";

const ACCENTS = {
  "the-locker-room": "text-svn-red",
  "taste-test": "text-gold",
  "good-company": "text-gold",
  "good-company-sunday": "text-gold",
  "signal-saturday": "text-svn-blue",
  "svn-espanol": "text-gold",
};

export default function Home() {
  const [homepage, setHomepage] = useState(null);
  const [shows, setShows] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/homepage"),
      api.get("/shows"),
      api.get("/episodes/latest", { params: { limit: 12 } }),
      api.get("/sponsors", { params: { placement: "home" } }),
    ]).then(([h, s, e, sp]) => {
      setHomepage(h.data);
      setShows(s.data);
      setEpisodes(e.data);
      setSponsors(sp.data);
    });
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the signal. Welcome to SVN.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Subscription failed");
    } finally {
      setSubscribing(false);
    }
  };

  if (!homepage) return <div className="min-h-screen flex items-center justify-center text-neutral-400">Loading…</div>;

  // Compute featured shows based on CMS list (fallback to all English shows)
  const featuredSlugs = homepage.featured_show_slugs?.length
    ? homepage.featured_show_slugs
    : shows.filter((s) => s.language !== "es").map((s) => s.slug);
  const featuredShows = featuredSlugs
    .map((slug) => shows.find((s) => s.slug === slug))
    .filter(Boolean);

  // Latest episodes: if CMS list set, honor it; else use the latest 6
  const latestEpisodes = homepage.latest_episode_ids?.length
    ? homepage.latest_episode_ids
        .map((id) => episodes.find((e) => e.id === id))
        .filter(Boolean)
    : episodes.slice(0, 6);

  // Sponsor spotlight: if CMS list set, honor it; else use all home-placement sponsors
  const spotlightSponsors = homepage.sponsor_spotlight_ids?.length
    ? homepage.sponsor_spotlight_ids.map((id) => sponsors.find((s) => s.id === id)).filter(Boolean)
    : sponsors;

  const lineup = (homepage.weekly_lineup?.length ? homepage.weekly_lineup : []).map((d) => {
    const show = shows.find((s) => s.slug === d.show_slug);
    return {
      day: d.day,
      label: d.label_override || show?.title || "—",
      slug: d.show_slug,
      accent: ACCENTS[d.show_slug] || "text-white",
    };
  });

  return (
    <div data-testid="home-page">
      <VideoModal episode={playing} onClose={() => setPlaying(null)} />

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center" data-testid="home-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/7301210/pexels-photo-7301210.jpeg"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-12 gap-12 items-center py-24">
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 bg-white/5 backdrop-blur text-[10px] font-bold uppercase tracking-[0.3em]">
              <Radio size={12} className="text-svn-red animate-pulse" />
              <span>{homepage.hero_eyebrow || "Live on the Signal"}</span>
            </div>
            <h1 className="font-display font-black mt-6 text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter">
              <span className="text-white">{homepage.hero_title_part1 || "SVN"}</span>
              <span className="text-svn-red"> {homepage.hero_title_part2 || "NETWORK"}</span>
            </h1>
            <p className="mt-6 font-display text-2xl md:text-4xl font-bold tracking-tight">
              {(homepage.tagline || "Different Voices. One Signal.").split(".").map((part, i, arr) => (
                <span key={i} className={i === arr.length - 2 ? "text-gold" : ""}>
                  {part}{i < arr.length - 1 ? "." : ""}{i < arr.length - 2 ? " " : ""}
                </span>
              ))}
            </p>
            <p className="mt-6 text-lg md:text-xl text-neutral-300 max-w-2xl font-body">
              {homepage.subtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to={homepage.primary_cta_link || "/shows"}
                data-testid="hero-watch-shows-btn"
                className="inline-flex items-center gap-2 bg-svn-red text-white font-black uppercase tracking-widest text-sm px-8 py-4 hover:bg-red-700 transition-colors"
              >
                <Play size={16} className="fill-white" /> {homepage.primary_cta_label || "Watch Shows"}
              </Link>
              <Link
                to={homepage.secondary_cta_link || "/episodes"}
                data-testid="hero-latest-episodes-btn"
                className="inline-flex items-center gap-2 border border-white/30 text-white font-black uppercase tracking-widest text-sm px-8 py-4 hover:bg-white/5 transition-colors"
              >
                {homepage.secondary_cta_label || "Latest Episodes"} <ChevronRight size={16} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="aspect-square relative">
              <img
                src={homepage.hero_artwork_url || SVN_LOGO}
                alt="SVN Network"
                className="w-full h-full object-cover border border-white/10 shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-gold text-black px-6 py-3 font-black uppercase tracking-widest text-xs">
                Featured Show
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WEEKLY LINEUP */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="home-weekly-lineup">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">
                The Weekly Signal
              </p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">
                {homepage.weekly_lineup_title || "Tune in every day."}
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {lineup.map((d) => (
              <Link
                to={d.slug ? `/shows/${d.slug}` : "/shows"}
                key={d.day}
                className="group bg-[#0F0F0F] border border-white/5 hover:border-white/30 p-5 transition-all hover:-translate-y-1"
                data-testid={`weekly-day-${d.day.toLowerCase()}`}
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500 group-hover:text-gold transition-colors">
                  {d.day}
                </p>
                <p className={`mt-3 font-display font-extrabold text-lg leading-tight ${d.accent}`}>
                  {d.label}
                </p>
                <div className="mt-6 text-xs text-neutral-500 group-hover:text-white inline-flex items-center gap-1">
                  Listen <ChevronRight size={12} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED SHOWS */}
      <section className="py-24 md:py-32 border-t border-white/5 bg-[#080808]" data-testid="home-featured-shows">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12 gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Featured Shows</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">
                Our network. <span className="text-svn-red">One voice for every story.</span>
              </h2>
            </div>
            <Link to="/shows" className="hidden md:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white hover:text-gold" data-testid="featured-shows-view-all">
              View All <ChevronRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredShows.map((s, i) => (
              <Link
                to={`/shows/${s.slug}`}
                key={s.id}
                className={`group relative overflow-hidden bg-[#0F0F0F] border border-white/5 hover:border-gold/40 transition-all ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}
                data-testid={`featured-show-${s.slug}`}
              >
                <div className={`relative ${i === 0 ? "aspect-[16/12]" : "aspect-square"} overflow-hidden`}>
                  <img src={s.artwork_url} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">{s.schedule.join(" • ")}</p>
                  <h3 className="mt-2 font-display font-black text-2xl md:text-3xl text-white">{s.title}</h3>
                  <p className="mt-1 text-sm text-neutral-300">{s.tagline}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST EPISODES */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="home-latest-episodes">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Latest Episodes</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Press play.</h2>
            </div>
            <Link to="/episodes" className="hidden md:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white hover:text-gold" data-testid="latest-episodes-view-all">
              All Episodes <ChevronRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestEpisodes.map((ep) => (
              <EpisodeCard key={ep.id} ep={ep} onPlay={setPlaying} />
            ))}
          </div>
        </div>
      </section>

      {/* SPONSOR SPOTLIGHT */}
      {spotlightSponsors.length > 0 && (
        <section className="py-20 border-y border-white/5 bg-[#080808] overflow-hidden" data-testid="home-sponsors">
          <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8 flex items-end justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-2">Sponsor Spotlight</p>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Trusted partners on the signal.</h2>
            </div>
            <Link to="/sponsors" className="hidden md:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white hover:text-gold" data-testid="sponsors-cta">
              Partner With Us <ChevronRight size={14} />
            </Link>
          </div>

          <div className="relative">
            <div className="flex gap-8 animate-marquee w-max">
              {[...spotlightSponsors, ...spotlightSponsors, ...spotlightSponsors].map((s, i) => (
                <a key={`${s.id}-${i}`} href={s.website_url || "#"} target="_blank" rel="noreferrer" className="shrink-0 w-[260px] bg-[#0F0F0F] border border-white/5 hover:border-gold/40 p-6 transition-colors" data-testid={`sponsor-marquee-${i}`}>
                  <div className="aspect-[4/3] flex items-center justify-center bg-black/40 mb-4">
                    {s.logo_url ? <img src={s.logo_url} alt={s.name} className="max-h-full max-w-full object-contain" /> : <span className="font-display font-black text-2xl text-gold">{s.name}</span>}
                  </div>
                  <p className="font-display font-bold text-white">{s.name}</p>
                  <p className="text-xs text-neutral-400 line-clamp-2 font-body mt-1">{s.description}</p>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MEMBERSHIP CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="home-membership-cta">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1708457753320-02e52a276a80?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4 inline-flex items-center gap-2"><Sparkles size={14} /> SVN+ Membership</p>
            <h2 className="font-display text-4xl md:text-6xl font-black tracking-tighter leading-none">
              Join the inner <span className="text-gold">signal</span>.
            </h2>
            <p className="mt-6 text-lg text-neutral-300 max-w-xl font-body">
              Early access. Bonus content. Exclusive discussions. A direct line to the network and our community.
            </p>
            <Link to="/membership" className="mt-10 inline-flex items-center gap-3 bg-gold text-black font-black uppercase tracking-widest text-sm px-8 py-4 hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all" data-testid="home-become-member-btn">
              Become A Member <ChevronRight size={16} />
            </Link>
          </div>
          <ul className="grid grid-cols-2 gap-4">
            {["Early Access", "Bonus Episodes", "Behind The Scenes", "Live Q&A", "Community", "Member Perks"].map((p) => (
              <li key={p} className="bg-black/60 backdrop-blur border border-gold/20 p-5">
                <p className="font-display font-bold text-white">{p}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="py-24 md:py-32 border-t border-white/5 bg-[#080808]" data-testid="home-newsletter">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4">The Signal Newsletter</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Stay connected to the signal.</h2>
          <p className="mt-4 text-neutral-300 font-body">New episodes, behind the scenes, drops and announcements — direct to your inbox.</p>
          <form onSubmit={subscribe} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto" data-testid="newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@svnlive.com" data-testid="newsletter-email-input" className="flex-1 bg-black border border-white/15 px-5 py-4 text-white placeholder:text-neutral-500 focus:outline-none focus:border-gold transition" />
            <button type="submit" disabled={subscribing} data-testid="newsletter-submit-btn" className="bg-svn-red text-white font-black uppercase tracking-widest text-sm px-8 py-4 hover:bg-red-700 transition disabled:opacity-60">
              {subscribing ? "Joining…" : "Subscribe"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
